import { AnnotationMode } from "pdfjs-dist/legacy/build/pdf";

export class CancelledError extends Error {
  constructor(message = "cancelled") {
    super(message);
    this.name = "CancelledError";
  }
}

export function isCancelled(err) {
  if (err instanceof CancelledError) return true;
  const name = err?.name;
  return (
    name === "RenderingCancelledException" ||
    name === "CancelledError" ||
    name === "AbortException"
  );
}

function cancellable(run) {
  let cancelled = false;
  let inCritical = 0;
  const hooks = [];
  let settleCancelled = null;

  const signal = {
    get cancelled() {
      return cancelled;
    },
    onCancel(fn) {
      if (cancelled) fn();
      else hooks.push(fn);
    },
    check() {
      if (cancelled) throw new CancelledError();
    },
    async critical(fn) {
      signal.check();
      inCritical++;
      try {
        return await fn();
      } finally {
        inCritical--;
        if (cancelled && inCritical === 0 && settleCancelled) settleCancelled();
      }
    },
  };

  const promise = new Promise((resolve, reject) => {
    let settled = false;
    const done = (fn) => {
      if (settled) return;
      settled = true;
      fn();
    };
    settleCancelled = () => done(() => reject(new CancelledError()));
    (async () => {
      signal.check();
      await run(signal);
      signal.check();
    })().then(
      () => done(resolve),
      (e) => done(() => reject(cancelled ? new CancelledError() : e)),
    );
  });

  return {
    promise,
    cancel() {
      if (cancelled) return;
      cancelled = true;
      for (const fn of hooks.splice(0)) {
        try {
          fn();
        } catch {}
      }
      if (inCritical === 0 && settleCancelled) settleCancelled();
    },
  };
}

const RANK = { visible: 0, idle: 1 };

class RenderQueue {
  constructor(maxConcurrent = 2) {
    this.maxConcurrent = maxConcurrent;
    this.waiting = [];
    this.running = new Set();
    this.order = 0;
  }

  run(priority, start) {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    const job = {
      rank: RANK[priority] ?? RANK.visible,
      order: this.order++,
      start,
      resolve,
      reject,
      running: null,
      done: false,
    };
    this.waiting.push(job);
    this.pump();
    return {
      promise,
      cancel: () => {
        if (job.done) return;
        if (job.running) {
          job.running.cancel();
          return;
        }
        this.waiting = this.waiting.filter((j) => j !== job);
        job.done = true;
        job.reject(new CancelledError());
      },
      promote: () => {
        if (job.done || job.running || job.rank === RANK.visible) return;
        job.rank = RANK.visible;
        this.pump();
      },
    };
  }

  pickNext() {
    if (this.waiting.length === 0 || this.running.size >= this.maxConcurrent) {
      return undefined;
    }
    let best;
    for (const j of this.waiting) {
      if (
        !best ||
        j.rank < best.rank ||
        (j.rank === best.rank && j.order < best.order)
      ) {
        best = j;
      }
    }
    if (best && best.rank === RANK.idle && this.running.size > 0)
      return undefined;
    return best;
  }

  pump() {
    for (;;) {
      const job = this.pickNext();
      if (!job) return;
      this.waiting = this.waiting.filter((j) => j !== job);
      this.running.add(job);
      let task;
      try {
        task = job.start();
      } catch (e) {
        this.finish(job);
        job.reject(e);
        continue;
      }
      job.running = task;
      task.promise.then(
        () => {
          this.finish(job);
          job.resolve();
        },
        (e) => {
          this.finish(job);
          job.reject(e);
        },
      );
    }
  }

  finish(job) {
    job.done = true;
    job.running = null;
    this.running.delete(job);
    // Settle callbacks run first; the next job starts in a microtask.
    Promise.resolve().then(() => this.pump());
  }
}

class ByteLru {
  constructor(budget) {
    this.budget = budget;
    this.entries = new Map();
    this.used = 0;
  }

  static bytes(v) {
    return Math.max(0, v.width) * Math.max(0, v.height) * 4;
  }

  get(key) {
    const v = this.entries.get(key);
    if (v) {
      this.entries.delete(key);
      this.entries.set(key, v);
    }
    return v;
  }

  has(key) {
    return this.entries.has(key);
  }

  put(key, v) {
    const size = ByteLru.bytes(v);
    const prev = this.entries.get(key);
    if (prev === v) return;
    if (prev) {
      this.entries.delete(key);
      this.used -= ByteLru.bytes(prev);
      prev.close();
    }
    if (size > this.budget / 2) {
      v.close();
      return;
    }
    this.entries.set(key, v);
    this.used += size;
    while (this.used > this.budget) {
      const [oldKey, old] = this.entries.entries().next().value;
      this.entries.delete(oldKey);
      this.used -= ByteLru.bytes(old);
      old.close();
    }
  }
}

const BITMAP_BUDGET_DESKTOP = 96 * 1024 * 1024;
const BITMAP_BUDGET_MOBILE = 32 * 1024 * 1024;
const MAX_CANVAS_PIXELS = 16777216;

function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

const renderQueue = new RenderQueue(2);
const bitmapCache = new ByteLru(
  isMobileDevice() ? BITMAP_BUDGET_MOBILE : BITMAP_BUDGET_DESKTOP,
);
const pageSizes = new Map();

const pageSizeKey = (docKey, pageNumber) => `${docKey}#${pageNumber}`;

function planFor(size, cssWidth) {
  const scale = cssWidth / size.width;
  let ratio =
    typeof window !== "undefined" && window.devicePixelRatio
      ? window.devicePixelRatio
      : 1;
  const pixels = size.width * scale * ratio * (size.height * scale * ratio);
  if (pixels > MAX_CANVAS_PIXELS)
    ratio *= Math.sqrt(MAX_CANVAS_PIXELS / pixels);
  const renderScale = scale * ratio;
  return {
    renderScale,
    canvasW: Math.floor(size.width * renderScale),
    canvasH: Math.floor(size.height * renderScale),
  };
}

const bitmapKey = (docKey, pageNumber, plan) =>
  `${docKey}|${pageNumber}|${plan.canvasW}x${plan.canvasH}`;

export function getCachedPage(docKey, pageNumber, cssWidth) {
  const size = pageSizes.get(pageSizeKey(docKey, pageNumber));
  if (!size) return null;
  const plan = planFor(size, cssWidth);
  const bitmap = bitmapCache.get(bitmapKey(docKey, pageNumber, plan));
  return bitmap ? { plan, source: bitmap } : null;
}

export function renderPage({ pdf, docKey, pageNumber, cssWidth, priority }) {
  let result = null;
  const job = renderQueue.run(priority, () =>
    cancellable(async (signal) => {
      const page = await pdf.getPage(pageNumber);
      signal.check();
      const base = page.getViewport({ scale: 1 });
      const size = { width: base.width, height: base.height };
      pageSizes.set(pageSizeKey(docKey, pageNumber), size);
      const plan = planFor(size, cssWidth);
      const key = bitmapKey(docKey, pageNumber, plan);
      const hit = bitmapCache.get(key);
      if (hit) {
        result = { plan, source: hit };
        return;
      }
      const canvas = document.createElement("canvas");
      try {
        await signal.critical(async () => {
          canvas.width = plan.canvasW;
          canvas.height = plan.canvasH;
          const task = page.render({
            canvasContext: canvas.getContext("2d"),
            viewport: page.getViewport({ scale: plan.renderScale }),
            annotationMode: AnnotationMode.ENABLE,
          });
          signal.onCancel(() => task.cancel());
          await task.promise;
        });
        signal.check();
      } catch (err) {
        releaseSource(canvas);
        throw err;
      }
      if (typeof createImageBitmap === "function") {
        createImageBitmap(canvas).then(
          (bmp) => bitmapCache.put(key, bmp),
          () => undefined,
        );
      }
      result = { plan, source: canvas };
    }),
  );
  return {
    promise: job.promise.then(() => result),
    cancel: job.cancel,
    promote: job.promote,
  };
}

export function releaseSource(source) {
  if (source && typeof source.getContext === "function") {
    source.width = 0;
    source.height = 0;
  }
}

export function drawPage(canvas, { plan, source }) {
  if (canvas.width !== plan.canvasW) canvas.width = plan.canvasW;
  if (canvas.height !== plan.canvasH) canvas.height = plan.canvasH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.drawImage(source, 0, 0);
}
